import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { DocumentIntelligenceViewer } from "@/features/document-intelligence/document-intelligence-viewer";
import { useProject } from "@/components/projects/project-context";
import { useRouteParam } from "@/lib/use-route-param";
import { ActionLogProvider } from "@/hooks/use-action-log-session";

export const Route = createFileRoute("/project/documents/$documentId")({
  component: ProjectDocumentDetailPage,
});

export function ProjectDocumentDetailPage() {
  const { switchProject, projectId, setProjectId } = useProject();
  const documentId = useRouteParam("documentId");

  const {
    data: documentDetails,
    isPending: isDocumentPending,
    isError: isDocumentError,
  } = useQuery(
    convexQuery(
      api.projectDocuments.getDocumentWithRevisions,
      documentId
        ? {
            documentId: documentId as Id<"projectDocuments">,
            projectId: projectId ? (projectId as Id<"projects">) : undefined,
          }
        : "skip"
    )
  );

  const effectiveProjectId =
    (documentDetails?.document.projectId as Id<"projects"> | undefined) ??
    (projectId ?? undefined);
  const { data: project, isPending: isProjectPending, isError: isProjectError } = useQuery(
    convexQuery(
      api.projects.getById,
      effectiveProjectId ? { projectId: effectiveProjectId } : "skip"
    )
  );

  useEffect(() => {
    if (!documentDetails) {
      return;
    }
    if (projectId === documentDetails.document.projectId) {
      return;
    }
    setProjectId(documentDetails.document.projectId);
  }, [documentDetails, projectId, setProjectId]);

  if (!documentId) {
    return (
      <div className="py-20 text-center text-muted-foreground">Invalid document route.</div>
    );
  }

  if (isProjectPending || isDocumentPending) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center rounded-xl border bg-background">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (isProjectError || isDocumentError || !project || !documentDetails) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          This document could not be found.
        </CardContent>
      </Card>
    );
  }

  return (
    <ActionLogProvider
      projectId={effectiveProjectId ?? null}
      pilot="DocPilot"
      targetKind="projectDocument"
      targetId={documentId}
    >
      <DocumentIntelligenceViewer
        projectName={project.name}
        documentName={documentDetails.document.name}
        revisions={documentDetails.revisions}
        onBack={() =>
          switchProject(project._id, {
            to: "/project/documents",
            title: "Project Documents",
          })
        }
      />
    </ActionLogProvider>
  );
}
